// Single source of truth for season and age-group constants.
//
// These were previously copy-pasted across api.js and the Schedule/Standings/
// Analytics/Admin pages, so a season rollover meant editing ~50 literals.
//
// SEASONS is a build-time fallback; the authoritative list comes from
// GET /api/seasons (see fetchSeasons) so the backend can add a season without a
// frontend deploy.

export const SEASONS = ['26/27', '25/26', '24/25'];

export const CURRENT_SEASON = SEASONS[0];

// ---------------------------------------------------------------------------
// What the app OPENS ON.
//
// Deliberately separate from CURRENT_SEASON, which is the season the pipeline
// scrapes and the API treats as current. The two differ while a new season is
// still filling in: 26/27 currently holds only 42 Academy fixtures at U13/U14,
// so opening there would show an empty app.
//
// The 26/27 schedule landed on 2026-08-24 — 24,628 fixtures — so these now
// point at the live season. 25/26 and 24/25 remain selectable.
// ---------------------------------------------------------------------------
export const DEFAULT_SEASON = '26/27';
export const DEFAULT_AGE_GROUP = 'U15';
export const DEFAULT_DIVISION_NAME = 'Homegrown Division';


// Every age group MLS NEXT fields. Note there is no U18 — U17 is followed by U19.
export const AGE_GROUPS = ['U13', 'U14', 'U15', 'U16', 'U17', 'U19'];

// Age groups that have a Flex bracket in addition to League play.
export const U15_PLUS = ['U15', 'U16', 'U17', 'U19'];

// Age groups MLS NEXT publishes Quality of Play ratings for. QoP does not exist
// for U15+ — this is an upstream limitation, not an unfinished feature.
export const QOP_AGE_GROUPS = ['U13', 'U14'];

// Short display labels for Game.competition_type. "league" is the common case and
// is left unbadged in the UI, so only the exceptions really matter here.
export const COMPETITION_LABELS = {
  league: 'League',
  flex: 'Flex',
  pro_player_pathway: 'Pro Player Pathway',
  other: 'Other',
};

// Division names carry a redundant " Division" suffix for compact UI ("Homegrown
// Division" was the widest filter pill at 186px, and the word repeats the name of
// the filter itself).
export const shortDivision = (name) =>
  typeof name === 'string' ? name.replace(/\s+Division$/i, '') : name;

// Season date window, mirroring data/seasons.py: September through mid-July.
export const seasonStartYear = (season) => 2000 + parseInt(String(season).slice(0, 2), 10);

export const seasonWindow = (season) => {
  const y = seasonStartYear(season);
  return { start: `${y}-09-01 00:00:00`, end: `${y + 1}-07-15 23:59:59` };
};

export const isSeasonInProgress = (season, now = new Date()) => {
  const { start, end } = seasonWindow(season);
  return now >= new Date(start.replace(' ', 'T')) && now <= new Date(end.replace(' ', 'T'));
};

// Time windows for the Schedule. Mid-season the full list runs to thousands of
// fixtures, so "what's on this week" meant paging through everything already played.
export const TIME_WINDOWS = [
  { value: 'week', label: 'This week' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'season', label: 'Full season' },
];

const pad = (n) => String(n).padStart(2, '0');
const stamp = (d, endOfDay) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${endOfDay ? '23:59:59' : '00:00:00'}`;

// Resolve a window to concrete dates. Returns {} for the whole season.
export const resolveTimeWindow = (windowValue, season, now = new Date()) => {
  if (windowValue === 'season') return {};
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (windowValue === 'week') {
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);
    return { startDate: stamp(today, false), endDate: stamp(weekEnd, true) };
  }
  // upcoming: from today to the end of the season
  return { startDate: stamp(today, false), endDate: seasonWindow(season).end };
};

// In-season, open on what's next; out of season, the whole thing.
export const defaultTimeWindow = (season, now = new Date()) =>
  isSeasonInProgress(season, now) ? 'upcoming' : 'season';

// A U15+ team plays League and Flex fixtures interleaved through the season, so
// filtering to one of them by default hides half of a player's calendar. It hid
// Springfield SYC's actual 26/27 season opener — a mid-September away Flex game
// at Sporting — behind a filter nobody had touched. The schedule now opens on
// everything; the pill is still there to narrow it.
//
// Standings deliberately do NOT follow this: League and Flex tables are
// separate competitions and merging them would be meaningless.
export const DEFAULT_COMPETITION = 'all';
